import { arrayMove } from '@dnd-kit/sortable';
import { format, parseISO } from 'date-fns';
import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import sprite from '../../../assets/icons/sprite.svg';
import { OrganisationContext } from '../../../context';
import { Channel, channelsNewApi } from '../../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  getAriaSort,
  getCssVar,
  getWebAppUrl,
  IconLabel,
  IconLabelHintDirection,
  InputField,
  Modal,
  normalizeSubdomain,
  OrderDirection,
  ReorderArgs,
  SearchField,
  SortableItem,
  SortableItemTag,
  SortableList,
  sortByDateAsc,
  sortByDateDesc,
  sortByNumberAsc,
  sortByNumberDesc,
  sortByStringAsc,
  sortByStringDesc,
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useIsNarrowViewport,
} from '../../../shared';
import {
  channelCreate,
  channelDelete,
  channelUpdate,
  updateChannelsPositionOnDrop,
} from '../../../slices';

import classNames from 'classnames';
import classes from './Channels.module.scss';

const CHANNEL_MIN_LENGTH = 1;

const CHANNEL_MAX_LENGTH = 60;

interface ArchiveChannelArgs {
  id: number;
  isArchived: boolean;
}

enum Columns {
  NAME = 'NAME',
  CURATORS = 'CURATORS',
  READERS = 'READERS',
  ACCESSED_AT = 'ACCESSED_AT',
  CREATED_AT = 'CREATED_AT',
}

interface SortChannelsArgs {
  orderBy: Columns;
  orderDirection: OrderDirection;
}

interface SortHeaderArgs {
  orderBy: Columns;
  label: string;
}

interface GetPinnedStatusArgs {
  id: number;
  channelPinnedStoryId?: number;
  channelPinnedStoryId2?: number;
}

interface ChannelClickArgs {
  subdomain: string;
  isArchived: boolean;
}

export const Channels: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardChannels' });

  const dispatch = useAppDispatch();

  const isNarrowViewport = useIsNarrowViewport();

  const [channelUpdatePosition] = channelsNewApi.endpoints.channelUpdatePosition.useMutation();

  const { organisation } = useContext(OrganisationContext);

  const { channels } = useAppSelector(({ channels }) => channels);

  const [channelsToDisplay, setChannelsToDisplay] = useState<Channel[]>([]);

  const [query, setQuery] = useState<string>('');

  const debouncedQuery = useDebounce<string>(query);

  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const [deleteChannelId, setDeleteChannelId] = useState<number | null>(null);

  const [archiveChannel, setArchiveChannel] = useState<ArchiveChannelArgs | null>(null);

  const [name, setName] = useState<string>('');

  const [subdomain, setSubdomain] = useState<string>('');

  const [showSubdomain, setShowSubdomain] = useState<boolean>(false);

  const [orderByActive, setOrderByActive] = useState<Columns | null>(null);

  const [orderDirectionActive, setOrderDirectionActive] = useState<OrderDirection | null>(null);

  const draggingDisabled = useMemo(
    () => Boolean(query.length || orderByActive || orderDirectionActive),
    [orderByActive, orderDirectionActive, query.length]
  );

  const channelIds = useMemo(() => channelsToDisplay.map(({ id }) => id), [channelsToDisplay]);

  const onModalClose = useCallback(() => {
    setModalOpened(false);
    setName('');
    setSubdomain('');
    setShowSubdomain(false);
  }, []);

  const onNameChange = useCallback(
    (value: string) => {
      setName(value);

      if (!showSubdomain) {
        setSubdomain(normalizeSubdomain(value));
      }
    },
    [showSubdomain]
  );

  const createChannelDisabled = useMemo(
    () => name.length < CHANNEL_MIN_LENGTH || subdomain.length < CHANNEL_MIN_LENGTH,
    [name.length, subdomain.length]
  );

  const channelDeleteHandler = useCallback(async () => {
    if (!deleteChannelId) {
      return;
    }

    const channelId = deleteChannelId;

    setDeleteChannelId(null);

    toast.loading(t('channelDeleteStatus.pending'), { toastId: 'channelDelete' });

    const payload = await dispatch(channelDelete({ channelId })).unwrap();

    toast.dismiss('channelDelete');

    if (payload) {
      toast.success(t('channelDeleteStatus.success'));
      return;
    }

    toast.error(t('common.error-message'));
  }, [deleteChannelId, dispatch, t]);

  const channelErrorHandler = useCallback(
    (errorType: string) => {
      switch (errorType) {
        case 'ChannelDuplicateSubdomainError':
          toast.error(t('channelError.duplicate-subdomain'));
          break;
        case 'ChannelSubdomainMalformedError':
          toast.error(t('channelError.subdomain-malformed'));
          break;
        case 'ChannelValidationError':
          toast.error(t('channelError.validation'));
          break;
        default:
          toast.error(t('common.error-message'));
      }
    },
    [t]
  );

  const channelCreateHandler = useCallback(async () => {
    onModalClose();

    toast.loading(t('channelCreateStatus.pending'), { toastId: 'channelCreate' });

    const { payload, error } = await dispatch(
      channelCreate({ fields: { name, subdomain } })
    ).unwrap();

    toast.dismiss('channelCreate');

    if (payload) {
      toast.success(t('channelCreateStatus.success'));
      return;
    }

    if (error) {
      channelErrorHandler(error.__typename);
    }
  }, [channelErrorHandler, dispatch, name, onModalClose, subdomain, t]);

  const channelArchiveToggle = useCallback(async () => {
    if (!archiveChannel) {
      return;
    }

    const { id, isArchived } = archiveChannel;

    setArchiveChannel(null);

    toast.loading(t('channelUpdateStatus.pending'), { toastId: 'channelUpdate' });

    const { payload, error } = await dispatch(
      channelUpdate({ channelId: id, fields: { isArchived: !isArchived } })
    ).unwrap();

    toast.dismiss('channelUpdate');

    if (payload) {
      toast.success(t('channelUpdateStatus.success'));
      return;
    }

    if (error) {
      channelErrorHandler(error.__typename);
    }
  }, [archiveChannel, channelErrorHandler, dispatch, t]);

  const modalTitle = ctxT('create-new');

  const modalBody = useMemo(() => {
    return (
      <div className={classes['channels__modal-content']}>
        <div className={classes['channels__modal-content-title']}>{modalTitle}</div>

        <InputField
          value={name}
          onChange={({ target }) => onNameChange(target.value)}
          label={ctxT('name')}
          placeholder={ctxT('name-placeholder')}
          maxLength={CHANNEL_MAX_LENGTH}
        />

        <IconLabel
          {...(!showSubdomain && { iconId: 'edit', iconSize: 18 })}
          label={getWebAppUrl(subdomain || 'yourchannel')}
          onClick={() => setShowSubdomain(true)}
          color={'#6b6e73'}
          nonClickable={showSubdomain}
          labelFirst
          singleColor
        />

        {showSubdomain && (
          <InputField
            value={subdomain}
            onChange={({ target }) => setSubdomain(normalizeSubdomain(target.value))}
            label={ctxT('subdomain')}
            placeholder={ctxT('subdomain-placeholder')}
            maxLength={CHANNEL_MAX_LENGTH}
          />
        )}

        <Button
          className={classes['channels__modal-content-button']}
          label={t('common.create')}
          type={ButtonType.primary}
          disabled={createChannelDisabled}
          onClick={channelCreateHandler}
        />
      </div>
    );
  }, [
    channelCreateHandler,
    createChannelDisabled,
    ctxT,
    modalTitle,
    name,
    onNameChange,
    showSubdomain,
    subdomain,
    t,
  ]);

  const sortByName = useCallback(
    (orderDirection: OrderDirection) => {
      return [...channels].sort(({ name: nameA }: Channel, { name: nameB }: Channel) =>
        orderDirection === OrderDirection.ASC
          ? sortByStringAsc(nameA, nameB)
          : sortByStringDesc(nameA, nameB)
      );
    },
    [channels]
  );

  const sortByCurators = useCallback(
    (orderDirection: OrderDirection) => {
      return [...channels].sort(
        ({ curatorsCount: curatorsCountA }: Channel, { curatorsCount: curatorsCountB }: Channel) =>
          orderDirection === OrderDirection.ASC
            ? sortByNumberAsc(curatorsCountA, curatorsCountB)
            : sortByNumberDesc(curatorsCountA, curatorsCountB)
      );
    },
    [channels]
  );

  const sortByReaders = useCallback(
    (orderDirection: OrderDirection) => {
      return [...channels].sort(
        ({ readersCount: readersCountA }: Channel, { readersCount: readersCountB }: Channel) =>
          orderDirection === OrderDirection.ASC
            ? sortByNumberAsc(readersCountA, readersCountB)
            : sortByNumberDesc(readersCountA, readersCountB)
      );
    },
    [channels]
  );

  const sortByAccessedAt = useCallback(
    (orderDirection: OrderDirection) => {
      return [...channels].sort(
        ({ accessedAt: accessedAtA }: Channel, { accessedAt: accessedAtB }: Channel) =>
          orderDirection === OrderDirection.ASC
            ? sortByDateAsc(accessedAtA, accessedAtB)
            : sortByDateDesc(accessedAtA, accessedAtB)
      );
    },
    [channels]
  );

  const sortByCreatedAt = useCallback(
    (orderDirection: OrderDirection) => {
      return [...channels].sort(
        ({ createdAt: createdAtA }: Channel, { createdAt: createdAtB }: Channel) =>
          orderDirection === OrderDirection.ASC
            ? sortByDateAsc(createdAtA, createdAtB)
            : sortByDateDesc(createdAtA, createdAtB)
      );
    },
    [channels]
  );

  const getSortedChannels = useCallback(
    ({ orderBy, orderDirection }: SortChannelsArgs) => {
      switch (orderBy) {
        case Columns.NAME:
          return sortByName(orderDirection);
        case Columns.CURATORS:
          return sortByCurators(orderDirection);
        case Columns.READERS:
          return sortByReaders(orderDirection);
        case Columns.ACCESSED_AT:
          return sortByAccessedAt(orderDirection);
        case Columns.CREATED_AT:
          return sortByCreatedAt(orderDirection);
        default:
          return [...channels];
      }
    },
    [channels, sortByAccessedAt, sortByCreatedAt, sortByCurators, sortByName, sortByReaders]
  );

  const sortBy = useCallback(
    (orderBy: Columns) => {
      const orderDirection =
        orderBy !== orderByActive
          ? OrderDirection.DESC
          : orderDirectionActive === OrderDirection.DESC
          ? OrderDirection.ASC
          : OrderDirection.DESC;

      setOrderByActive(orderBy);

      setOrderDirectionActive(orderDirection);

      setChannelsToDisplay(
        getSortedChannels({ orderBy, orderDirection }).filter(({ name }) =>
          name.toLowerCase().startsWith(query.toLowerCase())
        )
      );
    },
    [getSortedChannels, orderByActive, orderDirectionActive, query]
  );

  const sortHeader = useCallback(
    ({ orderBy, label }: SortHeaderArgs) => {
      const isSorted = orderBy === orderByActive;

      return (
        <th
          {...(isSorted &&
            orderDirectionActive && { 'aria-sort': getAriaSort(orderDirectionActive) })}
        >
          <button
            type={'button'}
            className={classes['channels__content-table-sort-header']}
            onClick={() => sortBy(orderBy)}
          >
            {label}
            {isSorted && (
              <svg className={classes['channels__content-table-sort']}>
                <use
                  href={`${sprite}#${
                    orderDirectionActive === OrderDirection.DESC ? 'down' : 'up'
                  }-arrow`}
                />
              </svg>
            )}
          </button>
        </th>
      );
    },
    [orderByActive, orderDirectionActive, sortBy]
  );

  useEffect(() => {
    setChannelsToDisplay(
      (orderByActive && orderDirectionActive
        ? [...getSortedChannels({ orderBy: orderByActive, orderDirection: orderDirectionActive })]
        : [...channels]
      ).filter(({ name }) => name.toLowerCase().startsWith(query.toLowerCase()))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, debouncedQuery]);

  const getPinnedStatus = useCallback(
    ({ id, channelPinnedStoryId, channelPinnedStoryId2 }: GetPinnedStatusArgs) => {
      if (id === channelPinnedStoryId || id === channelPinnedStoryId2) {
        return t('common.channel');
      }

      if (id === organisation.pinnedStoryId) {
        return t('common.organisation');
      }
    },
    [organisation.pinnedStoryId, t]
  );

  const reorderHandler = useCallback(
    async ({ activeId, overId }: ReorderArgs) => {
      const activeIndex = channelsToDisplay.findIndex(({ id }) => id === activeId);

      const overIndex = channelsToDisplay.findIndex(({ id }) => id === overId);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      const { position } = channelsToDisplay[overIndex];

      setChannelsToDisplay(arrayMove(channelsToDisplay, activeIndex, overIndex));

      dispatch(
        updateChannelsPositionOnDrop({ draggingChannelId: activeId, targetChannelId: overId })
      );

      await channelUpdatePosition({ channelId: activeId, position }).unwrap();
    },
    [channelUpdatePosition, channelsToDisplay, dispatch]
  );

  const resetFiltersHandler = useCallback(() => {
    setQuery('');
    setOrderByActive(null);
    setOrderDirectionActive(null);
    setChannelsToDisplay(channels);
  }, [channels]);

  const channelClickHandler = useCallback(
    ({ subdomain, isArchived }: ChannelClickArgs) => {
      if (isArchived) {
        toast.info(ctxT('archived-click-message'));
        return;
      }
      window.open(`${getWebAppUrl(subdomain)}/admin`, '_blank', 'noopener, noreferrer');
    },
    [ctxT]
  );

  return (
    <div className={classes['channels']}>
      <h1 className={classes['channels__title']}>{t('orgLayoutMenu.channels')}</h1>
      <div className={classes['channels__intro']}>{ctxT('intro')}</div>
      <div className={classes['channels__content']}>
        <div className={classes['channels__content-header']}>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t('common.type-to-search')}
            className={classes['channels__content-header-search']}
          />

          {draggingDisabled && (
            <IconLabel
              iconId={'info'}
              iconSize={20}
              label={ctxT('reset-sort-and-filter-label')}
              data={ctxT('reset-sort-and-filter-label-hint')}
              hintDirection={IconLabelHintDirection.top}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={resetFiltersHandler}
              labelFirst
            />
          )}

          <Button
            className={classes['channels__content-header-button']}
            label={t('common.create')}
            type={ButtonType.primary}
            onClick={() => setModalOpened(true)}
          />
        </div>

        <div
          className={classNames(classes['channels__content-table-wrapper'], {
            [classes['channels__content-table-wrapper--narrow']]: isNarrowViewport,
          })}
        >
          <SortableList items={channelIds} onReorder={reorderHandler} disabled={draggingDisabled}>
            <table className={classes['channels__content-table']}>
              <thead>
                <tr>
                  {sortHeader({ orderBy: Columns.NAME, label: ctxT('name') })}
                  {sortHeader({ orderBy: Columns.CURATORS, label: ctxT('curators') })}
                  {sortHeader({ orderBy: Columns.READERS, label: ctxT('readers') })}
                  {sortHeader({ orderBy: Columns.ACCESSED_AT, label: ctxT('updated') })}
                  {sortHeader({ orderBy: Columns.CREATED_AT, label: ctxT('created') })}
                  <th>{ctxT('pinned')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {channelsToDisplay.map(
                  ({
                    id,
                    subdomain,
                    name,
                    isArchived,
                    curatorsCount,
                    readersCount,
                    accessedAt,
                    createdAt,
                    pinnedStories,
                    pinnedStoryId,
                    pinnedStoryId2,
                  }) => (
                    <SortableItem
                      as={SortableItemTag.TR}
                      key={id}
                      id={id}
                      className={classNames({
                        [classes['channels__content-table-row--move']]: !draggingDisabled,
                      })}
                    >
                      <td>
                        <button
                          type={'button'}
                          className={classes['channels__content-table-name']}
                          onClick={() => channelClickHandler({ subdomain, isArchived })}
                        >
                          {name}
                        </button>
                        {isArchived && (
                          <>
                            &nbsp;
                            <span className={classes['channels__content-table-archived']}>
                              {ctxT('archived')}
                            </span>
                          </>
                        )}
                      </td>
                      <td>{curatorsCount}</td>
                      <td>{readersCount}</td>
                      <td>{format(parseISO(accessedAt), 'MMM dd, yyyy')}</td>
                      <td>{format(parseISO(createdAt), 'MMM dd, yyyy')}</td>
                      <td>
                        {pinnedStories
                          .map(({ id }) =>
                            getPinnedStatus({
                              id,
                              channelPinnedStoryId: pinnedStoryId,
                              channelPinnedStoryId2: pinnedStoryId2,
                            })
                          )
                          .join(', ')}
                      </td>
                      <td className={classes['channels__content-table-actions']}>
                        <IconLabel
                          iconId={`archive${isArchived ? '-restore' : ''}`}
                          iconSize={20}
                          ariaLabel={ctxT(`${isArchived ? 'un' : ''}archive`)}
                          color={'#5f6368'}
                          hoverColor={'#43445b'}
                          onClick={() => setArchiveChannel({ id, isArchived })}
                        />
                        <IconLabel
                          iconId={'delete'}
                          iconSize={20}
                          ariaLabel={t('common.delete')}
                          color={getCssVar('--color-danger')}
                          hoverColor={getCssVar('--color-danger-hover')}
                          onClick={() => setDeleteChannelId(id)}
                        />
                      </td>
                    </SortableItem>
                  )
                )}
              </tbody>
            </table>
          </SortableList>
        </div>
      </div>

      <Modal
        isOpen={modalOpened}
        body={modalBody}
        onClose={onModalClose}
        ariaLabel={modalTitle}
        alignTop
      />

      <ConfirmationModal
        isOpen={Boolean(deleteChannelId)}
        onClose={() => setDeleteChannelId(null)}
        acceptLabel={t('common.delete')}
        onAccept={channelDeleteHandler}
        title={ctxT('delete-title')}
        subTitle={ctxT('delete-sub-title')}
        danger
      />

      <ConfirmationModal
        isOpen={Boolean(archiveChannel)}
        onClose={() => setArchiveChannel(null)}
        onAccept={channelArchiveToggle}
        acceptLabel={ctxT(`${archiveChannel?.isArchived ? 'un' : ''}archive`)}
        title={ctxT(`${archiveChannel?.isArchived ? 'un' : ''}archive-title`)}
        subTitle={ctxT(`${archiveChannel?.isArchived ? 'un' : ''}archive-sub-title`)}
      />
    </div>
  );
};
